import './style.modules.scss'

import { BrandIcon, BrandIconColor, BrandIconId } from '@ampcore/adel-react'
import React, { useEffect, useState } from 'react'
import xss, { whiteList } from 'xss'

import { Redirector } from '../Redirector/Redirector'
import {
  RedirectConfig,
  RedirectorVariant
} from '../Redirector/Redirector.type'

export type InterstitialProps = {
  heading?: string
  redirectUrl?: string
  body?: string
  disclaimer?: string
  selectedItem?: string
  countdown?: string | number
  source?: string
  redirectorConfig?: RedirectConfig[]
  shortUrl?: string
  testId?: string
  /**
   * Suppresses the direct `redirectUrl` navigation that fires when the
   * countdown reaches zero, so the component can be mounted in isolation (e.g.
   * a docs/preview harness) without the browser leaving the page. Has no effect
   * on the `redirectorConfig`/`shortUrl` paths, which delegate to Redirector.
   */
  disableRedirect?: boolean
}

export const Interstitial = (props: InterstitialProps) => {
  const DEFAULT_COUNTDOWN = 6 // Default countdown value in seconds
  const {
    heading,
    body,
    disclaimer,
    countdown,
    redirectUrl,
    redirectorConfig,
    source,
    selectedItem,
    shortUrl,
    testId,
    disableRedirect
  } = props

  const [remainingSeconds, setRemainingSeconds] = useState(
    Number(countdown ?? DEFAULT_COUNTDOWN)
  )
  const [redirectNow, setRedirectNow] = useState(false)
  const [isCancelled, setIsCancelled] = useState(false)

  useEffect(() => {
    const interval = setInterval(() => {
      setRemainingSeconds(prev => {
        if (isCancelled) {
          clearInterval(interval)
          return prev
        }
        return prev <= 1 ? 0 : prev - 1
      })
    }, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [isCancelled])

  const handleCancel = () => {
    setIsCancelled(true) // Stop the countdown
  }

  const handleContinue = () => {
    setRedirectNow(true)
  }

  if (shortUrl) {
    // The short/<source>/<shortUrl> route sends source='scheduler' to resolve
    // an invitation; every other source is a short-url lookup.
    if (source === 'scheduler') {
      return (
        <Redirector
          variant={RedirectorVariant.INVITATION}
          invitationKey={shortUrl}
        />
      )
    }
    return (
      <Redirector variant={RedirectorVariant.SHORT_URL} shortUrl={shortUrl} />
    )
  }

  // Seamless transition: a config-driven redirect is analytics/route-only and
  // never shows the interstitial shell. Delegate immediately and render nothing
  // visible, independent of the countdown.
  if (redirectorConfig) {
    return (
      <Redirector
        variant={RedirectorVariant.SEAMLESS}
        config={redirectorConfig}
        selectedItem={selectedItem}
        source={source}
      />
    )
  }

  if (
    (remainingSeconds === 0 || redirectNow) &&
    redirectUrl &&
    !disableRedirect
  ) {
    if (window.parent) {
      window.parent.location.replace(redirectUrl)
    } else {
      window.location.replace(redirectUrl)
    }
  }

  const renderHeading = () => {
    if (!heading) return null
    const countdownSpan = (
      <span
        id="interstitial-countdown"
        data-testid={testId ? `${testId}-countdown` : undefined}
      >
        {remainingSeconds}
      </span>
    )
    if (heading.includes('%countdown%')) {
      const parts = heading.split('%countdown%')
      return (
        <h3>
          {parts[0]}
          {countdownSpan}
          {parts[1]}
        </h3>
      )
    }
    return <h3>{heading}</h3>
  }

  return (
    <section className="Interstitial">
      <div className="u-bgColorAmpfBlueLight Content--reversed">
        <div className="u-flex u-sizeConstrained u-paddingVert32">
          <div className="u-flex u-sizeFull u-flexJustifyCenter">
            {renderHeading()}
          </div>
        </div>
      </div>
      <div className="u-sizeConstrained" data-testid={testId}>
        <div className="u-marginVert32 u-marginLeft20">
          <BrandIcon
            icon={BrandIconId.AMPF_LOGO}
            color={BrandIconColor.AMERIPRISE_BLUE}
            fill={true}
            style={{ width: '150px', height: '52px' }}
          />
        </div>
        <div
          className="u-borderTop u-borderBottom u-paddingVert32 Content"
          dangerouslySetInnerHTML={{ __html: xss(body ?? '') }}
          data-testid={testId ? `${testId}-body` : undefined}
        />

        <div className="u-flex u-flexJustifyBetween u-margin24">
          <button
            onClick={handleContinue}
            className="Button Button--primary continue-button"
            data-testid={testId ? `${testId}-continue-button` : undefined}
            data-tracking="Interstitial page Link - continue"
            disabled={!redirectUrl}
          >
            Continue
          </button>
          <button
            onClick={handleCancel}
            className="Button Button--secondary Button--secondary--action-blue cancel-button"
            disabled={isCancelled}
            data-testid={testId ? `${testId}-cancel-button` : undefined}
            data-tracking="Interstitial page Link - cancel"
          >
            Cancel
          </button>
        </div>
        <footer className="u-paddingBottom20">
          {disclaimer && (
            <div
              className="Content u-paddingVert8"
              dangerouslySetInnerHTML={{
                __html: xss(disclaimer, {
                  whiteList: {
                    ...whiteList,
                    div: ['class'],
                    p: ['class'],
                    a: ['href', 'class', 'data-analytics']
                  }
                })
              }}
              data-testid={testId ? `${testId}-disclaimer` : undefined}
            />
          )}
        </footer>
      </div>
      <div className="u-bgColorAmpfBlueLight u-padding16 u-marginTop48"></div>
    </section>
  )
}
